import {Container, Image, Panel9Slice, Text} from '@wonderlandengine/react-ui/components';

import React, {useMemo} from 'react';
import {parseColor} from '../utils/utils.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {FlatOpaque} from '../../types/materials.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {StoreInventoryItems} from '../../enums/StoreInventoryItems.js';
import {Texture} from '@wonderlandengine/api';
import {Align, Justify, PositionType} from '@wonderlandengine/react-ui';
import {useCoconutBomb, useFruitHammer} from '../hooks/usePowerup.js';
import {UiTexturesManager} from './ui-textures-manager.js';
import {AdManager, AdRewardTypes} from '../../components/ad-manager.js';

export interface PowerupButtonAssignment {
    powerupStat?: TransactionalNames;
    powerupButton: React.ReactNode; // This represents the power-up's unique component
    image?: Texture;
    storeInventoryItem?: StoreInventoryItems;
}

function adAvailable(): boolean {
    return AdManager.instance.isAdAvailable(AdRewardTypes.RemoveCooldown);
}

const CoconutBombButton = () => {
    const [cooldown, inventory, inventoryColor, onClick, cooldownMat, watchAd] =
        useCoconutBomb();

    return (
        <Container onClick={() => onClick()}>
            <Image
                material={cooldownMat}
                width={80}
                height={80}
                rounding={0}
                src={UiTexturesManager.instance.buttonBuyBomb}
            ></Image>
            {cooldown > 0 && adAvailable() && (
                <Container
                    position={PositionType.Absolute}
                    width={80}
                    height={80}
                    justifyContent={Justify.FlexStart}
                    alignItems={Align.FlexStart}
                >
                    <Image
                        z={0.001}
                        width={50}
                        height={50}
                        rounding={0}
                        src={UiTexturesManager.instance.buttonAdClean}
                        onClick={watchAd}
                    ></Image>
                </Container>
            )}
            <Container
                position={PositionType.Absolute}
                width={80}
                justifyContent={Justify.FlexEnd}
                height={72}
            >
                <Text
                    width={72}
                    textAlign="right"
                    color={inventoryColor}
                    z={0.001}
                    fontSize={24}
                >
                    {inventory}
                </Text>
            </Container>
        </Container>
    );
};
const FruitSmasherButton = () => {
    const [cooldown, inventory, inventoryColor, onClick, cooldownMat, watchAd] =
        useFruitHammer();

    return (
        <Container onClick={() => onClick()}>
            <Image
                material={cooldownMat}
                width={80}
                height={80}
                rounding={0}
                src={UiTexturesManager.instance.buttonFruitsmasher}
            ></Image>
            {cooldown > 0 && adAvailable() && (
                <Container
                    position={PositionType.Absolute}
                    width={80}
                    height={80}
                    justifyContent={Justify.FlexStart}
                    alignItems={Align.FlexStart}
                    padding={5}
                >
                    <Image
                        z={0.001}
                        width={50}
                        height={50}
                        rounding={0}
                        src={UiTexturesManager.instance.buttonAdClean}
                        onClick={watchAd}
                    ></Image>
                </Container>
            )}
            <Container
                position={PositionType.Absolute}
                width={80}
                justifyContent={Justify.FlexEnd}
                height={72}
            >
                <Text
                    width={72}
                    textAlign="right"
                    color={inventoryColor}
                    z={0.001}
                    fontSize={24}
                >
                    {inventory}
                </Text>
            </Container>
        </Container>
    );
};
const LockedButton = () => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mat = useMemo(() => {
        const mat = rotbMats.buttonMaterial.clone() as FlatOpaque;
        mat.setColor(parseColor(colorSwatch.PowerupLocked));
        return mat;
    }, []);
    return (
        <Container>
            <Panel9Slice
                width={80}
                height={80}
                material={mat}
                borderSize={4}
                borderTextureSize={0.06}
                rounding={0}
                padding={15}
            >
                <Image
                    width={50}
                    height={50}
                    rounding={0}
                    src={UiTexturesManager.instance.lock}
                ></Image>
            </Panel9Slice>
        </Container>
    );
};

export const PowerupButton: React.FC<{powerUp?: PowerupButtonAssignment}> = ({powerUp}) => {
    return <Container>{powerUp && powerUp.powerupButton}</Container>;
};

export const powerupButtonAssignments: PowerupButtonAssignment[] = [
    {powerupButton: <LockedButton />},
    {
        powerupStat: TransactionalNames.BombUpgrade,
        powerupButton: <CoconutBombButton />,
        storeInventoryItem: StoreInventoryItems.coconut,
        image: UiTexturesManager.instance?.buttonBuyBomb,
    },
    {
        powerupStat: TransactionalNames.FruitSmasherUpgrade,
        powerupButton: <FruitSmasherButton />,
        storeInventoryItem: StoreInventoryItems.smasher,
        image: UiTexturesManager.instance?.buttonFruitsmasher,
    },
    // { id: 2, name: 'Shield', unlocked: false, component: <Shield /> },
    // { id: 3, name: 'Double Points', unlocked: true, component: <DoublePoints /> },
    // { id: 4, name: 'Invisibility', unlocked: false, component: <Invisibility /> },
];
